// QR Table Pay › Templates (SCR-107) and the staff QR Templates page (SCR-024; legacy QRTableTemplates.vue,
// pages/shared/qr-templates, `qr_code_templates`; field note backoffice.qr-table-pay): printable table toppers drawn on a
// Konva page. A location's templates are its own; the premade set is shared by every account and written only on the
// staff page (B875: legacy let any merchant user write it, and change another store's templates by id). A template keeps
// its shapes as legacy's Konva attributes, but never a picture of its QR code: the editor draws the code from the store's
// address and the template's table (B873: a stored picture went missing and blocked every later save).
//
// Cloud-only, as the QR codes: templates are designed in the cloud back office, and the premade set spans accounts.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { LocationId, QrTemplateId } from '../../primitives/codecs.ts';
import { QrCodeTable } from './qr.ts';

const HexColor = z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

/** Legacy `QR_CODE_FLYER_SIZE` labels: 4x6 (384×576), 5x8 (480×786) and 8x11 (786×1056) canvas pixels. */
export const QrTemplatePageSize = z.enum(['four_by_six', 'five_by_eight', 'eight_by_eleven']).meta({ id: 'QrTemplatePageSize' });

/** Legacy `QR_CODE_FLYER_SHAPES`. */
export const QrTemplateShapeKind = z
  .enum(['qr-text', 'qr-image', 'qr-divider', 'qr-arrow', 'qr-square', 'qr-circle', 'qr-polygon', 'qr-star', 'qr-code', 'qr-table-id'])
  .meta({ id: 'QrTemplateShapeKind' });

/** A Konva attribute as legacy stores it: a number, text (an uploaded image as a data URL), a flag, or a list of numbers (points, dash). */
const KonvaAttribute = z.union([z.number(), z.string().max(700_000), z.boolean(), z.array(z.number()).max(400), z.null()]);

/**
 * One shape on the page: its Konva attributes as legacy saves them (`x`, `y`, `width`, `height`, `rotation`, `fill`,
 * `stroke`, `text`, `fontSize`, `points`, `cornerRadius`…), keyed by its `name` (a uid) with its kind and layer label.
 */
export const QrTemplateShape = z
  .object({
    name: z.string().min(1).max(64),
    shape_type: QrTemplateShapeKind,
    /** The layer's name in the Layers tab (legacy renames cap it at 15 characters; new shapes get 10 random letters). */
    shape_label: z.string().max(64).optional(),
    className: z.string().max(32),
  })
  .catchall(KonvaAttribute)
  .meta({ id: 'QrTemplateShape' });

export const QrTemplateChanges = z
  .object({
    name: z.string().trim().min(1).max(255),
    pageSize: QrTemplatePageSize,
    /** The page's colour (legacy `page_background_color`, default white). */
    pageBackgroundColor: HexColor,
    /** The QR code's light colour (legacy `background_color`, default white). */
    backgroundColor: HexColor,
    /** The QR code's modules (legacy `foreground_color`, default black; B876: legacy drew them swapped). */
    foregroundColor: HexColor,
    /** The QR code's quiet zone in modules. */
    margin: z.int().min(0).max(100),
    /** The table the QR code and Table ID name; a premade has none. */
    table: QrCodeTable.nullable(),
    /** The shapes, bottom layer first. */
    content: z.array(QrTemplateShape).max(200),
  })
  .meta({ id: 'QrTemplateChanges' });

export const QrTemplateView = QrTemplateChanges.extend({
  qrTemplateId: QrTemplateId,
  /** The location's own template, or null for a premade one. */
  locationId: LocationId.nullable(),
  premade: z.boolean(),
  updatedAt: z.iso.datetime(),
}).meta({ id: 'QrTemplateView' });

export const QrTemplateNotFound = defineError({
  code: 'online.qr_template_not_found', httpStatus: 404, message: 'errors.online.qr_template_not_found', retryable: false,
  details: z.object({ qrTemplateId: QrTemplateId }),
});

/**
 * A save the server refuses: `table_required` (a location's template names its table, as legacy's API required),
 * `table_not_found` (not a live table of the location), `one_qr_code` / `one_table_id` (B874: one of each per template).
 */
export const QrTemplateInvalid = defineError({
  code: 'online.qr_template_invalid', httpStatus: 422, message: 'errors.online.qr_template_invalid', retryable: false,
  details: z.object({ rule: z.string(), field: z.string().optional() }),
});

const cloudOnly = {
  availability: 'cloud', origin: 'cloud',
  cloudReason: 'QR templates are designed in the cloud back office, and the premade set is shared by every account.',
  emits: [], dryRun: true, risk: 'low', agent: { expose: false },
} as const;

export const UpsertQrTemplate = defineCommand({
  ...cloudOnly,
  name: 'online.upsertQrTemplate',
  summary: "Create or change one of the location's QR templates (a premade is copied into a new one of the location's own).",
  input: z.object({ qrTemplateId: QrTemplateId, locationId: LocationId, template: QrTemplateChanges }),
  output: QrTemplateView,
  permission: 'qr.manage',
  errors: [QrTemplateInvalid, QrTemplateNotFound],
  legacy: ['POST /v1/online-store/{id}/qr-code-templates', 'PUT /v1/online-store/{id}/qr-code-templates/{template}'],
});

export const DeleteQrTemplate = defineCommand({
  ...cloudOnly,
  name: 'online.deleteQrTemplate',
  summary: "Delete one of the location's QR templates.",
  input: z.object({ qrTemplateId: QrTemplateId, locationId: LocationId }),
  output: z.object({ id: QrTemplateId, deleted: z.literal(true) }),
  permission: 'qr.manage',
  errors: [QrTemplateNotFound],
  legacy: ['DELETE /v1/online-store/{id}/qr-code-templates/{template}'],
});

export const UpsertPremadeQrTemplate = defineCommand({
  ...cloudOnly,
  name: 'online.upsertPremadeQrTemplate',
  summary: 'Create or change a premade QR template, which every merchant can start from. Radish staff and resellers only.',
  input: z.object({ qrTemplateId: QrTemplateId, template: QrTemplateChanges }),
  output: QrTemplateView,
  permission: 'qr.premade.manage',
  errors: [QrTemplateInvalid, QrTemplateNotFound],
  legacy: ['POST /v1/qr-code-templates', 'PUT /v1/qr-code-templates/{template}'],
});

export const DeletePremadeQrTemplate = defineCommand({
  ...cloudOnly,
  name: 'online.deletePremadeQrTemplate',
  summary: 'Delete a premade QR template. Radish staff and resellers only.',
  input: z.object({ qrTemplateId: QrTemplateId }),
  output: z.object({ id: QrTemplateId, deleted: z.literal(true) }),
  permission: 'qr.premade.manage',
  errors: [QrTemplateNotFound],
  legacy: ['DELETE /v1/qr-code-templates/{template}'],
});

export const ListQrTemplates = defineQuery({
  name: 'online.qrTemplates.list',
  summary: "The location's QR templates, newest first, and the premade set it can start from.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ templates: z.array(QrTemplateView), premade: z.array(QrTemplateView) }),
  permission: 'qr.manage',
  availability: 'cloud',
  cloudReason: 'QR templates are designed in the cloud back office, and the premade set is shared by every account.',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/online-store/{id}/qr-code-templates', 'GET /v1/online-store/{id}/qr-code-templates/{template}/content', 'GET /v1/qr-code-templates'],
});

export const ListPremadeQrTemplates = defineQuery({
  name: 'online.premadeQrTemplates.list',
  summary: 'Every premade QR template, newest first, for the staff QR Templates page.',
  input: z.object({}),
  output: z.object({ premade: z.array(QrTemplateView) }),
  permission: 'qr.premade.manage',
  availability: 'cloud',
  cloudReason: 'QR templates are designed in the cloud back office, and the premade set is shared by every account.',
  scope: 'account',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/qr-code-templates', 'GET /v1/qr-code-templates/{template}/content'],
});

export const qrTemplateCommands = [UpsertQrTemplate, DeleteQrTemplate, UpsertPremadeQrTemplate, DeletePremadeQrTemplate] as const;
export const qrTemplateQueries = [ListQrTemplates, ListPremadeQrTemplates] as const;
